import { Box, ButtonBase, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import React, { useEffect, useState } from "react";
import theme from "Theme/AppTheme";
import { EmissionsConfigurationType } from "./EmissionsChips";

type Props = {
    isOpen: boolean;
    canCreateBody: boolean;
    onClose: () => void;
    onContinue: (type: EmissionsConfigurationType) => void;
};

const options: {
    type: EmissionsConfigurationType;
    title: string;
    description: string;
    source: string;
}[] = [
    {
        type: "endpoint",
        title: "Endpoint",
        description:
            "Where emissions are sent and how we authenticate — token endpoint, endpoint URI, client credentials, retry policy.",
        source: "Outbound Messaging Controller",
    },
    {
        type: "body",
        title: "Emission Body",
        description:
            "The JSON request body we build from a conversation and send. Must be linked to an endpoint.",
        source: "Emissions Configuration Controller",
    },
];

const NewConfigurationDialog: React.FC<Props> = ({
    isOpen,
    canCreateBody,
    onClose,
    onContinue,
}) => {
    const [selected, setSelected] =
        useState<EmissionsConfigurationType>("endpoint");

    useEffect(() => {
        if (isOpen) setSelected("endpoint");
    }, [isOpen]);

    return (
        <AcxDialog
            isOpen={isOpen}
            onClose={onClose}
            title="New configuration"
            subTitle="What are you setting up?"
            maxWidth="sm"
            fullWidth
            dialogContentChildren={
                <Box role="radiogroup" sx={{ display: "flex", gap: "14px" }}>
                    {options.map((option) => {
                        const isDisabled =
                            option.type === "body" && !canCreateBody;
                        const isSelected = selected === option.type;
                        return (
                            <ButtonBase
                                key={option.type}
                                role="radio"
                                aria-checked={isSelected}
                                disabled={isDisabled}
                                onClick={() => setSelected(option.type)}
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "stretch",
                                    gap: "8px",
                                    textAlign: "left",
                                    padding: "15px",
                                    borderRadius: "10px",
                                    border: isSelected
                                        ? `2px solid ${theme.palette.primary.main}`
                                        : `2px solid ${theme.palette.gray[200]}`,
                                    backgroundColor: isSelected
                                        ? theme.palette.primary[50]
                                        : theme.palette.white.main,
                                    opacity: isDisabled ? 0.5 : 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                            boxSizing: "border-box",
                                            flex: "0 0 auto",
                                            border: isSelected
                                                ? `5px solid ${theme.palette.primary.main}`
                                                : `2px solid ${theme.palette.gray[300]}`,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 700,
                                        }}
                                    >
                                        {option.title}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        lineHeight: 1.55,
                                        color: theme.palette.gray[600],
                                    }}
                                >
                                    {option.description}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: "11px",
                                        color: theme.palette.gray[400],
                                    }}
                                >
                                    {isDisabled
                                        ? "Create an active endpoint first."
                                        : option.source}
                                </Typography>
                            </ButtonBase>
                        );
                    })}
                </Box>
            }
        >
            <AcxButton
                variant="contained"
                color="primary"
                onClick={() => onContinue(selected)}
            >
                Continue
            </AcxButton>
        </AcxDialog>
    );
};

export default NewConfigurationDialog;
